import type { AgentTypicalFlow, Company, SystemSettings } from "./types";

export type OutboundTaskOriginType = "csv" | "api";

export type OutboundPlaybookStatus =
	| "draft"
	| "enabled"
	| "disabled"
	| "archived";

export type OutboundPlaybookRunStatus =
	| "pending"
	| "scheduled"
	| "running"
	| "paused"
	| "completed"
	| "partially_failed"
	| "failed"
	| "cancelled";

export type OutboundTaskStatus =
	| "pending"
	| "scheduled"
	| "dispatching"
	| "in_progress"
	| "retry_wait"
	| "paused"
	| "completed"
	| "failed"
	| "cancelled";

export type OutboundTaskAttemptStatus =
	| "claimed"
	| "starting"
	| "in_progress"
	| "completed"
	| "failed"
	| "abandoned"
	| "cancelled";

export type OutboundWorkerStatus =
	| "available"
	| "busy"
	| "draining"
	| "offline"
	| "disabled";

export type OutboundTaskAttemptOutcome =
	| "confirmed"
	| "cancelled"
	| "changes_requested"
	| "qualified"
	| "not_qualified"
	| "no_answer"
	| "wrong_number"
	| "failed"
	| "custom";

export type OutboundTimestamp = Date | string | null;

export type OutboundPayload = Record<string, unknown>;

export type OutboundSchemaFieldType =
	| "string"
	| "number"
	| "boolean"
	| "date"
	| "datetime"
	| "phone_number";

export interface OutboundSchemaFieldDefinition {
	name: string;
	type: OutboundSchemaFieldType;
	required: boolean;
	description?: string;
	enumValues?: string[];
	columnName?: string;
	showInReport?: boolean;
}

export const OUTBOUND_SCHEMA_FIELD_TYPES = [
	"string",
	"number",
	"boolean",
	"date",
	"datetime",
	"phone_number",
] as const satisfies readonly OutboundSchemaFieldType[];

export const DEFAULT_OUTBOUND_INPUT_SCHEMA = {
	type: "object",
	required: ["phoneNumber"],
	properties: {
		phoneNumber: {
			type: "string",
			format: "phone_number",
			description: "Destination phone number in E.164 format.",
		},
		contactName: {
			type: "string",
			description: "Name of the person to call.",
		},
	},
	additionalProperties: true,
};

export const DEFAULT_OUTBOUND_OUTPUT_SCHEMA = {
	type: "object",
	required: ["outcome", "summary"],
	properties: {
		outcome: {
			type: "string",
			enum: [
				"confirmed",
				"cancelled",
				"changes_requested",
				"qualified",
				"not_qualified",
				"no_answer",
				"wrong_number",
				"failed",
				"custom",
			],
			description: "Final structured outcome for the outbound task.",
		},
		summary: {
			type: "string",
			description: "Short report of what happened during the call.",
		},
		notes: {
			type: "string",
			description: "Additional details collected by the agent.",
		},
	},
	additionalProperties: true,
};

export function outboundPlaybookUseCaseKey(
	playbookName: string,
	playbookId?: string | null,
): string {
	const sourceName = playbookName.trim();
	const slug =
		sourceName
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "")
			.slice(0, 48) || "playbook";
	const hashSource = `${sourceName}\n${playbookId ?? ""}`;
	let hash = 0;
	for (let index = 0; index < hashSource.length; index += 1) {
		hash = (Math.imul(hash, 31) + hashSource.charCodeAt(index)) | 0;
	}
	const suffix = (hash >>> 0).toString(16).padStart(8, "0");
	return `${slug}-${suffix}`;
}

function schemaTypeForField(type: OutboundSchemaFieldType) {
	if (type === "date") return { type: "string", format: "date" };
	if (type === "datetime") return { type: "string", format: "date-time" };
	if (type === "phone_number")
		return { type: "string", format: "phone_number" };
	return { type };
}

export function buildJsonSchemaFromFields(
	fields: OutboundSchemaFieldDefinition[],
): OutboundPayload {
	const properties = Object.fromEntries(
		fields
			.map((field) => ({
				...field,
				name: field.name.trim(),
				description: field.description?.trim(),
				columnName: field.columnName?.trim(),
			}))
			.filter((field) => field.name)
			.map((field) => [
				field.name,
				{
					...schemaTypeForField(field.type),
					...(field.description ? { description: field.description } : {}),
					...(field.columnName ? { columnName: field.columnName } : {}),
					...(field.showInReport !== undefined
						? { showInReport: field.showInReport }
						: {}),
					...(field.type === "string" &&
					field.enumValues &&
					field.enumValues.length > 0
						? { enum: field.enumValues }
						: {}),
				},
			]),
	);
	const required = fields
		.filter((field) => field.required && field.name.trim())
		.map((field) => field.name.trim());

	return {
		type: "object",
		properties,
		...(required.length > 0 ? { required } : {}),
		additionalProperties: true,
	};
}

export function fieldsFromJsonSchema(
	schema: OutboundPayload,
): OutboundSchemaFieldDefinition[] {
	const properties =
		schema.properties && typeof schema.properties === "object"
			? (schema.properties as Record<string, Record<string, unknown>>)
			: {};
	const required = Array.isArray(schema.required)
		? schema.required.filter(
				(value): value is string => typeof value === "string",
			)
		: [];

	return Object.entries(properties).map(([name, definition]) => {
		const type =
			definition.format === "date"
				? "date"
				: definition.format === "date-time"
					? "datetime"
					: definition.format === "phone_number" ||
							definition.format === "phone-number" ||
							definition.type === "phone_number"
						? "phone_number"
						: definition.type === "number" ||
								definition.type === "boolean" ||
								definition.type === "string"
							? definition.type
							: "string";

		return {
			name,
			type,
			required: required.includes(name),
			description:
				typeof definition.description === "string"
					? definition.description
					: "",
			columnName:
				typeof definition.columnName === "string" ? definition.columnName : "",
			enumValues: Array.isArray(definition.enum)
				? definition.enum.filter(
						(value): value is string => typeof value === "string",
					)
				: [],
			showInReport:
				typeof definition.showInReport === "boolean"
					? definition.showInReport
					: true,
		};
	});
}

/**
 * Checks if a phone number string (raw or normalized) is a valid local Israeli phone number.
 * Outbound calls are limited to local Israeli numbers with prefixes 972-2, 3, 4, 5, 7, 8, 9.
 */
export function isLocalIsraelPhoneNumber(value: unknown): boolean {
	if (value === undefined || value === null) return false;

	let strVal = String(value).trim();
	if (!strVal) return false;

	if (typeof value === "number") {
		strVal = String(value);
	}

	const cleaned = strVal.replace(/[\s\-().]/g, "");
	if (!cleaned) return false;

	let normalized: string | null = null;

	if (cleaned.startsWith("+")) {
		const digits = cleaned.slice(1);
		if (/^\d{7,15}$/.test(digits)) {
			normalized = `+${digits}`;
		}
	} else {
		const digits = cleaned.replace(/\D/g, "");
		if (digits) {
			if (/^0[2345789]\d{7,8}$/.test(digits)) {
				normalized = `+972${digits.slice(1)}`;
			} else if (/^[57]\d{8}$/.test(digits)) {
				normalized = `+972${digits}`;
			} else if (/^[23489]\d{7}$/.test(digits)) {
				normalized = `+972${digits}`;
			} else if (/^972\d{8,9}$/.test(digits)) {
				normalized = `+${digits}`;
			}
		}
	}

	if (!normalized) return false;

	// E.164 format for Israeli local numbers (972-2/3/4/5/7/8/9):
	// - +9722XXXXXXX (Jerusalem 02: 8 digits after 972)
	// - +9723XXXXXXX (Tel Aviv 03: 8 digits after 972)
	// - +9724XXXXXXX (North 04: 8 digits after 972)
	// - +9728XXXXXXX (South 08: 8 digits after 972)
	// - +9729XXXXXXX (Sharon 09: 8 digits after 972)
	// - +9725XXXXXXXX (Mobile 05X: 9 digits after 972)
	// - +9727XXXXXXXX (VoIP/Landline 07X: 9 digits after 972)
	return /^\+972(?:[23489]\d{7}|[57]\d{8})$/.test(normalized);
}

export const isAllowedOutboundPhoneNumber = isLocalIsraelPhoneNumber;

export function parsePhoneNumberValue(value: unknown): string | null {
	if (value === undefined || value === null) return null;

	let strVal = String(value).trim();
	if (!strVal) return null;

	if (typeof value === "number") {
		strVal = String(value);
	}

	const cleaned = strVal.replace(/[\s\-().]/g, "");
	if (!cleaned) return null;

	let normalized: string | null = null;

	if (cleaned.startsWith("+")) {
		const digits = cleaned.slice(1);
		if (/^\d{7,15}$/.test(digits)) {
			normalized = `+${digits}`;
		}
	} else {
		const digits = cleaned.replace(/\D/g, "");
		if (digits) {
			if (/^0[2345789]\d{7,8}$/.test(digits)) {
				normalized = `+972${digits.slice(1)}`;
			} else if (/^[57]\d{8}$/.test(digits)) {
				normalized = `+972${digits}`;
			} else if (/^[23489]\d{7}$/.test(digits)) {
				normalized = `+972${digits}`;
			} else if (/^972\d{8,9}$/.test(digits)) {
				normalized = `+${digits}`;
			}
		}
	}

	if (normalized && isLocalIsraelPhoneNumber(normalized)) {
		return normalized;
	}

	return null;
}

export interface OutboundBusinessHoursWindow {
	dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
	startTime: string;
	endTime: string;
}

export interface OutboundRetryPolicy {
	maxAttempts: number;
	backoffSeconds: number[];
}

export interface OutboundCallConfiguration {
	agentId?: string | null;
	serviceId?: string | null;
	fromPhoneNumber?: string | null;
	workerType: "outbound_call";
}

export interface OutboundPlaybook {
	id?: string;
	path?: string;
	companyId: string;
	name: string;
	description?: string | null;
	greeting?: string | null;
	status: OutboundPlaybookStatus;
	version: number;
	conversationInstructions: string;
	typicalFlows?: AgentTypicalFlow[];
	inputSchema: OutboundPayload;
	resultSchema: OutboundPayload;
	timezone: string;
	businessHours: OutboundBusinessHoursWindow[];
	initialDelaySeconds: number;
	retryPolicy: OutboundRetryPolicy;
	terminalOutcomes: OutboundTaskAttemptOutcome[];
	callConfiguration: OutboundCallConfiguration;
	createdAt?: OutboundTimestamp;
	updatedAt?: OutboundTimestamp;
	enabledAt?: OutboundTimestamp;
	disabledAt?: OutboundTimestamp;
	archivedAt?: OutboundTimestamp;
	createdBy?: string | null;
	updatedBy?: string | null;
}

export interface OutboundPlaybookSnapshot {
	playbookId: string;
	playbookVersion: number;
	name: string;
	description?: string | null;
	greeting?: string | null;
	conversationInstructions: string;
	typicalFlows?: AgentTypicalFlow[];
	inputSchema: OutboundPayload;
	resultSchema: OutboundPayload;
	timezone: string;
	businessHours: OutboundBusinessHoursWindow[];
	initialDelaySeconds: number;
	retryPolicy: OutboundRetryPolicy;
	terminalOutcomes: OutboundTaskAttemptOutcome[];
	callConfiguration: OutboundCallConfiguration;
}

export interface OutboundPlaybookRunOrigin {
	type: OutboundTaskOriginType;
	externalId?: string | null;
	idempotencyKey?: string | null;
	submittedBy?: string | null;
	submittedAt?: OutboundTimestamp;
	metadata: OutboundPayload;
}

export interface OutboundPlaybookRun {
	id?: string;
	path?: string;
	companyId: string;
	playbookId: string;
	playbookVersion: number;
	status: OutboundPlaybookRunStatus;
	origin: OutboundPlaybookRunOrigin;
	playbookSnapshot: OutboundPlaybookSnapshot;
	taskCounts: {
		total: number;
		pending: number;
		scheduled: number;
		inProgress: number;
		paused: number;
		completed: number;
		failed: number;
		cancelled: number;
	};
	createdAt?: OutboundTimestamp;
	updatedAt?: OutboundTimestamp;
	startedAt?: OutboundTimestamp;
	completedAt?: OutboundTimestamp;
	cancelledAt?: OutboundTimestamp;
	lastTaskStateChangedAt?: OutboundTimestamp;
	error?: string | null;
}

export interface OutboundTask {
	id?: string;
	path?: string;
	companyId: string;
	playbookRunId: string;
	playbookId: string;
	playbookVersion: number;
	agentId?: string | null;
	recipientPhoneNumber?: string | null;
	status: OutboundTaskStatus;
	originType: OutboundTaskOriginType;
	input: OutboundPayload;
	result?: OutboundPayload | null;
	latestAttemptId?: string | null;
	latestAttemptStatus?: OutboundTaskAttemptStatus | null;
	latestOutcome?: OutboundTaskAttemptOutcome | null;
	attemptCount: number;
	maxAttempts: number;
	nextScheduledAt?: OutboundTimestamp;
	schedulingReason?: string | null;
	workerType: "outbound_call";
	claimedByWorkerId?: string | null;
	leaseExpiresAt?: OutboundTimestamp;
	createdAt?: OutboundTimestamp;
	updatedAt?: OutboundTimestamp;
	startedAt?: OutboundTimestamp;
	completedAt?: OutboundTimestamp;
	failedAt?: OutboundTimestamp;
	cancelledAt?: OutboundTimestamp;
	error?: string | null;
	errorCode?: string | null;
	recoveryReview?: {
		reason: string;
		detectedAt: OutboundTimestamp;
	} | null;
}

export interface OutboundTaskAttempt {
	id?: string;
	path?: string;
	companyId: string;
	taskId: string;
	playbookRunId: string;
	playbookId: string;
	playbookVersion: number;
	workerId: string;
	status: OutboundTaskAttemptStatus;
	attemptNumber: number;
	provider: "twilio_livekit";
	providerOpaqueId?: string | null;
	providerCreateRequestedAt?: OutboundTimestamp | null;
	providerCallContext?: {
		toPhoneNumber: string;
		fromPhoneNumber: string;
		agentId: string;
		serviceId: string | null;
		agentCallAuth: {
			tokenHash: string;
			roomId: string | null;
			active: boolean;
			issuedAt: OutboundTimestamp;
			expiresAt: OutboundTimestamp;
			revokedAt: OutboundTimestamp;
		};
	} | null;
	providerCallSid?: string | null;
	providerRoomName?: string | null;
	providerRoomSid?: string | null;
	outcome?: OutboundTaskAttemptOutcome | null;
	summary?: string | null;
	structuredResult?: OutboundPayload | null;
	transcriptRef?: string | null;
	recordingRef?: string | null;
	error?: string | null;
	errorCode?: string | null;
	createdAt?: OutboundTimestamp;
	updatedAt?: OutboundTimestamp;
	startedAt?: OutboundTimestamp;
	completedAt?: OutboundTimestamp;
	failedAt?: OutboundTimestamp;
	heartbeatAt?: OutboundTimestamp;
}

export interface OutboundWorker {
	id?: string;
	path?: string;
	companyId: string;
	status: OutboundWorkerStatus;
	workerType: "outbound_call";
	capabilities: string[];
	currentTaskId?: string | null;
	currentTaskAttemptId?: string | null;
	leaseExpiresAt?: OutboundTimestamp;
	heartbeatAt?: OutboundTimestamp;
	createdAt?: OutboundTimestamp;
	updatedAt?: OutboundTimestamp;
	disabledAt?: OutboundTimestamp;
	error?: string | null;
}

export interface DispatchOutboundTasksForCompanyRequest {
	companyId: string;
	limit?: number;
	workerId?: string;
	leaseSeconds?: number;
}

export interface DispatchedOutboundTaskResult {
	taskId: string;
	taskAttemptId: string;
	playbookRunId: string;
	attemptNumber: number;
	workerId: string;
	leaseExpiresAt: string;
}

export interface SkippedOutboundTaskDispatchResult {
	taskId?: string;
	reason: string;
}

export interface DispatchOutboundTasksForCompanyResponse {
	success: boolean;
	dispatched: DispatchedOutboundTaskResult[];
	skipped: SkippedOutboundTaskDispatchResult[];
}

export interface OutboundPlaybookRunRowResult {
	rowIndex: number;
	status: "created" | "failed";
	taskId?: string;
	errors?: string[];
}

export interface CreateOutboundPlaybookRunForCompanyRequest {
	companyId: string;
	playbookId: string;
	input?: OutboundPayload;
	taskInputs?: OutboundPayload[];
	idempotencyKey: string;
	externalId?: string;
	scheduleImmediately?: boolean;
}

export interface CreateOutboundPlaybookRunForCompanyResponse {
	success: boolean;
	duplicate: boolean;
	runId: string;
	taskIds: string[];
	totalRows?: number;
	createdCount?: number;
	failedCount?: number;
	rowResults?: OutboundPlaybookRunRowResult[];
}

export interface ForceRetryOutboundTaskForCompanyRequest {
	companyId: string;
	taskId: string;
}

export interface ForceRetryOutboundTaskForCompanyResponse {
	success: boolean;
	taskId: string;
	taskAttemptId: string;
	playbookRunId: string;
	attemptNumber: number;
	leaseExpiresAt: string;
}

export interface ScheduleOutboundTaskForCompanyRequest {
	companyId: string;
	taskId: string;
}

export interface ScheduleOutboundTaskForCompanyResponse {
	success: boolean;
	taskId: string;
	playbookRunId: string;
	nextScheduledAt: string;
	schedulingReason: string;
}

export interface PauseOutboundPlaybookRunForCompanyRequest {
	companyId: string;
	playbookRunId: string;
}

export interface ResumeOutboundPlaybookRunForCompanyRequest {
	companyId: string;
	playbookRunId: string;
}

export interface CancelOutboundPlaybookRunForCompanyRequest {
	companyId: string;
	playbookRunId: string;
}

export interface ControlOutboundPlaybookRunForCompanyResponse {
	success: boolean;
	playbookRunId: string;
	status: OutboundPlaybookRunStatus;
}

export const OUTBOUND_PLAYBOOK_RUN_TERMINAL_STATUSES = [
	"completed",
	"partially_failed",
	"failed",
	"cancelled",
] as const satisfies readonly OutboundPlaybookRunStatus[];

export const OUTBOUND_TASK_TERMINAL_STATUSES = [
	"completed",
	"failed",
	"cancelled",
] as const satisfies readonly OutboundTaskStatus[];

export const OUTBOUND_TASK_ATTEMPT_TERMINAL_STATUSES = [
	"completed",
	"failed",
	"abandoned",
	"cancelled",
] as const satisfies readonly OutboundTaskAttemptStatus[];

export const OUTBOUND_WORKER_ACTIVE_STATUSES = [
	"available",
	"busy",
	"draining",
] as const satisfies readonly OutboundWorkerStatus[];

export const OUTBOUND_PLAYBOOK_RUN_TRANSITIONS = {
	pending: ["scheduled", "running", "paused", "failed", "cancelled"],
	scheduled: [
		"running",
		"paused",
		"completed",
		"partially_failed",
		"failed",
		"cancelled",
	],
	running: ["paused", "completed", "partially_failed", "failed", "cancelled"],
	paused: ["scheduled", "running", "cancelled"],
	completed: [],
	partially_failed: [],
	failed: [],
	cancelled: [],
} as const satisfies Record<
	OutboundPlaybookRunStatus,
	readonly OutboundPlaybookRunStatus[]
>;

export const OUTBOUND_TASK_TRANSITIONS = {
	pending: ["scheduled", "dispatching", "paused", "cancelled", "failed"],
	scheduled: ["dispatching", "retry_wait", "paused", "cancelled", "failed"],
	dispatching: [
		"in_progress",
		"scheduled",
		"retry_wait",
		"failed",
		"cancelled",
	],
	in_progress: ["completed", "retry_wait", "failed", "cancelled"],
	retry_wait: ["scheduled", "dispatching", "paused", "failed", "cancelled"],
	paused: ["scheduled", "cancelled"],
	completed: [],
	failed: [],
	cancelled: [],
} as const satisfies Record<OutboundTaskStatus, readonly OutboundTaskStatus[]>;

export const OUTBOUND_TASK_ATTEMPT_TRANSITIONS = {
	claimed: ["starting", "in_progress", "failed", "abandoned", "cancelled"],
	starting: ["in_progress", "completed", "failed", "abandoned", "cancelled"],
	in_progress: ["completed", "failed", "abandoned", "cancelled"],
	completed: [],
	failed: [],
	abandoned: [],
	cancelled: [],
} as const satisfies Record<
	OutboundTaskAttemptStatus,
	readonly OutboundTaskAttemptStatus[]
>;

export function isOutboundPlaybookRunTerminal(
	status: OutboundPlaybookRunStatus,
): boolean {
	return OUTBOUND_PLAYBOOK_RUN_TERMINAL_STATUSES.includes(
		status as (typeof OUTBOUND_PLAYBOOK_RUN_TERMINAL_STATUSES)[number],
	);
}

export function isOutboundTaskTerminal(status: OutboundTaskStatus): boolean {
	return OUTBOUND_TASK_TERMINAL_STATUSES.includes(
		status as (typeof OUTBOUND_TASK_TERMINAL_STATUSES)[number],
	);
}

export function isOutboundTaskAttemptTerminal(
	status: OutboundTaskAttemptStatus,
): boolean {
	return OUTBOUND_TASK_ATTEMPT_TERMINAL_STATUSES.includes(
		status as (typeof OUTBOUND_TASK_ATTEMPT_TERMINAL_STATUSES)[number],
	);
}

export function canTransitionOutboundTask(
	from: OutboundTaskStatus,
	to: OutboundTaskStatus,
): boolean {
	const allowedTransitions: readonly OutboundTaskStatus[] =
		OUTBOUND_TASK_TRANSITIONS[from];
	return allowedTransitions.includes(to);
}

export function resolveOutboundPlaybookCallsEnabled(
	systemSettings?: Pick<SystemSettings, "outboundPlaybookCalls"> | null,
	company?: Pick<Company, "features"> | null,
): boolean {
	const settings = systemSettings?.outboundPlaybookCalls;
	if (settings?.disabledForAll === true) return false;
	const companyOverride = company?.features?.outboundPlaybookCalls;
	if (typeof companyOverride === "boolean") return companyOverride;
	return settings?.defaultEnabled !== false;
}
